import { useEffect, useState } from "react";
import { api, euro, formatDate, TRANSPARENCY_CATEGORIES, VISIBILITY_LABELS, ROLE_LABELS, API } from "@/lib/api";
import { Wallet, Plus, TrendingUp, TrendingDown, Trash2, FileText, Upload, Download, FileSignature, Eye, EyeOff, Users as UsersIcon, Pencil } from "lucide-react";
import { toast } from "sonner";

const DOC_ADMIN_ROLES = new Set(["consiglio", "tesoriere", "segretario", "fondatori"]);
const MINUTES_ADMIN_ROLES = new Set(["consiglio", "segretario"]);

export default function Transparency({ user, isAdmin, canManageExpenses }) {
  const [tab, setTab] = useState("bilancio");
  const canManageDocs = isAdmin || DOC_ADMIN_ROLES.has(user?.role);
  const canManageMinutes = isAdmin || MINUTES_ADMIN_ROLES.has(user?.role);
  // Fallback if prop not provided (e.g., direct use): owner + tesoriere
  const canEditBudget = canManageExpenses ?? (user?.is_admin || user?.role === "tesoriere");

  return (
    <div data-testid="section-transparency">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-[#EF3E36] font-bold flex items-center gap-2"><Wallet className="w-3 h-3" /> Trasparenza</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#2B3A33] mt-1">Amunì Trasparente</h1>
        <p className="text-[#6B6558] mt-2">Bilancio, documenti ufficiali e verbali delle riunioni. Un'Associazione a cielo aperto.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-[#E8E2D8]">
        <TabBtn id="bilancio" tab={tab} setTab={setTab} icon={Wallet} label="Bilancio & Spese" />
        <TabBtn id="documenti" tab={tab} setTab={setTab} icon={FileText} label="Documenti Ufficiali" />
        <TabBtn id="verbali" tab={tab} setTab={setTab} icon={FileSignature} label="Verbali Riunioni" />
      </div>

      {tab === "bilancio" && <ExpensesPanel isAdmin={canEditBudget} />}
      {tab === "documenti" && <DocumentsPanel canManage={canManageDocs} />}
      {tab === "verbali" && <VerbaliPanel canManage={canManageMinutes} />}
    </div>
  );
}

function TabBtn({ id, tab, setTab, icon: Icon, label }) {
  const active = tab === id;
  return (
    <button
      data-testid={`transparency-tab-${id}`}
      onClick={() => setTab(id)}
      className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-t-lg -mb-px border-b-2 transition ${
        active ? "text-[#EF3E36] border-[#EF3E36]" : "text-[#6B6558] border-transparent hover:text-[#2B3A33]"
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

/* ============ BILANCIO / EXPENSES ============ */
function ExpensesPanel({ isAdmin }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [wallet, setWallet] = useState("");
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({ entrate: 0, uscite: 0, saldo: 0, count: 0, years: [], cassa: {entrate:0,uscite:0,saldo:0}, banca: {entrate:0,uscite:0,saldo:0} });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    description: "", category: "", kind: "uscita", wallet: "cassa", amount: "", fiscal_year: year,
  });

  const load = () => {
    const walletQs = wallet ? `&wallet=${wallet}` : "";
    api.get(`/expenses?fiscal_year=${year}${walletQs}`).then(r => setExpenses(r.data));
    api.get(`/expenses/summary?fiscal_year=${year}`).then(r => setSummary(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, wallet]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/expenses", { ...form, amount: parseFloat(form.amount), fiscal_year: parseInt(form.fiscal_year) });
      setShowForm(false);
      setForm(f => ({ ...f, description: "", category: "", amount: "" }));
      toast.success("Movimento salvato"); load();
    } catch { toast.error("Errore salvataggio"); }
  };
  const del = async (id) => { await api.delete(`/expenses/${id}`); toast.success("Rimosso"); load(); };
  const yearsAvailable = summary.years.length ? summary.years : [year];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <select data-testid="wallet-filter" value={wallet} onChange={e => setWallet(e.target.value)} className="px-4 py-2.5 rounded-xl border border-[#E8E2D8] bg-white">
            <option value="">Tutti i portafogli</option>
            <option value="cassa">Solo Cassa (Contanti)</option>
            <option value="banca">Solo Banca (POS/Bonifico)</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="year-select" value={year} onChange={e => setYear(parseInt(e.target.value))} className="px-4 py-2.5 rounded-xl border border-[#E8E2D8] bg-white">
            {yearsAvailable.concat([year]).filter((v, i, a) => a.indexOf(v) === i).map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {isAdmin && (
            <button data-testid="new-expense-btn" onClick={() => setShowForm(v => !v)} className="terracotta-btn px-4 py-2.5 rounded-xl inline-flex items-center gap-2 font-semibold">
              <Plus className="w-4 h-4" /> Aggiungi
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div className="paper rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold flex items-center gap-1"><TrendingUp className="w-3 h-3 text-[#16A34A]" /> Entrate</div>
          <div className="font-display text-3xl font-bold text-[#16A34A] mt-2">{euro(summary.entrate)}</div>
        </div>
        <div className="paper rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold flex items-center gap-1"><TrendingDown className="w-3 h-3 text-[#EF3E36]" /> Uscite</div>
          <div className="font-display text-3xl font-bold text-[#EF3E36] mt-2">{euro(summary.uscite)}</div>
        </div>
        <div className="paper rounded-2xl p-5" style={{background: summary.saldo >= 0 ? "linear-gradient(180deg, #ECFCCB, #FFFFFF)" : "linear-gradient(180deg, #FEE2E2, #FFFFFF)"}}>
          <div className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Saldo</div>
          <div className={`font-display text-3xl font-bold mt-2 ${summary.saldo >= 0 ? "text-[#2B3A33]" : "text-[#EF3E36]"}`}>{euro(summary.saldo)}</div>
        </div>
      </div>

      {/* Cassa vs Banca split cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div data-testid="wallet-cassa" className="paper rounded-2xl p-5 border-l-4 border-[#EF3E36]">
          <div className="text-xs uppercase tracking-widest text-[#EF3E36] font-bold">💵 Cassa (Contanti)</div>
          <div className="mt-2 flex justify-between text-sm">
            <span className="text-[#16A34A] font-mono">+{euro(summary.cassa?.entrate || 0)}</span>
            <span className="text-[#EF3E36] font-mono">−{euro(summary.cassa?.uscite || 0)}</span>
          </div>
          <div className={`mt-1 font-display text-2xl font-bold ${(summary.cassa?.saldo || 0) >= 0 ? "text-[#2B3A33]" : "text-[#EF3E36]"}`}>Saldo: {euro(summary.cassa?.saldo || 0)}</div>
        </div>
        <div data-testid="wallet-banca" className="paper rounded-2xl p-5 border-l-4 border-[#1E40AF]">
          <div className="text-xs uppercase tracking-widest text-[#1E40AF] font-bold">🏦 Banca (POS / Bonifico)</div>
          <div className="mt-2 flex justify-between text-sm">
            <span className="text-[#16A34A] font-mono">+{euro(summary.banca?.entrate || 0)}</span>
            <span className="text-[#EF3E36] font-mono">−{euro(summary.banca?.uscite || 0)}</span>
          </div>
          <div className={`mt-1 font-display text-2xl font-bold ${(summary.banca?.saldo || 0) >= 0 ? "text-[#2B3A33]" : "text-[#EF3E36]"}`}>Saldo: {euro(summary.banca?.saldo || 0)}</div>
        </div>
      </div>

      {isAdmin && showForm && (
        <form onSubmit={submit} className="paper p-6 rounded-2xl mb-6 space-y-3" data-testid="expense-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input required data-testid="exp-date" type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
            <select data-testid="exp-kind" value={form.kind} onChange={e => setForm(f => ({...f, kind: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8] bg-white">
              <option value="entrata">Entrata</option>
              <option value="uscita">Uscita</option>
            </select>
            <select data-testid="exp-wallet" value={form.wallet} onChange={e => setForm(f => ({...f, wallet: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8] bg-white">
              <option value="cassa">💵 Cassa</option>
              <option value="banca">🏦 Banca</option>
            </select>
          </div>
          <input required data-testid="exp-desc" placeholder="Descrizione" value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input required data-testid="exp-category" placeholder="Categoria (es. Affitto)" value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
            <input required data-testid="exp-amount" placeholder="Importo €" type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
            <input required data-testid="exp-year" type="number" placeholder="Anno" value={form.fiscal_year} onChange={e => setForm(f => ({...f, fiscal_year: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
          </div>
          <div className="flex gap-2">
            <button type="submit" data-testid="exp-submit" className="terracotta-btn px-5 py-2.5 rounded-xl font-semibold">Salva</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2.5 rounded-xl border border-[#E8E2D8]">Annulla</button>
          </div>
        </form>
      )}

      <div className="paper rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-0 sm:min-w-[640px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-[#8A8272] border-b border-[#E8E2D8]">
              <th className="p-4">Data</th>
              <th className="p-4">Descrizione</th>
              <th className="p-4 hidden sm:table-cell">Categoria</th>
              <th className="p-4">Wallet</th>
              <th className="p-4 text-right">Importo</th>
              {isAdmin && <th className="p-4"></th>}
            </tr>
          </thead>
          <tbody>
            {expenses.length === 0 && (
              <tr><td colSpan={isAdmin ? 6 : 5} className="p-8 text-center text-[#6B6558]"><FileText className="w-6 h-6 mx-auto text-[#8A8272] mb-2" />Nessun movimento registrato per {year}.</td></tr>
            )}
            {expenses.map(x => {
              const w = x.wallet || "cassa";
              return (
                <tr key={x.id} data-testid={`expense-${x.id}`} className="border-b border-[#E8E2D8]/60">
                  <td className="p-4 text-[#6B6558]">{formatDate(x.date)}</td>
                  <td className="p-4 font-medium text-[#2B3A33]">{x.description}</td>
                  <td className="p-4 text-[#6B6558] hidden sm:table-cell">{x.category}</td>
                  <td className="p-4">
                    <span className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ${w === "cassa" ? "bg-[#FDF2F2] text-[#EF3E36]" : "bg-[#DBEAFE] text-[#1E40AF]"}`}>
                      {w === "cassa" ? "💵 Cassa" : "🏦 Banca"}
                    </span>
                  </td>
                  <td className={`p-4 text-right font-mono font-bold ${x.kind === "entrata" ? "text-[#16A34A]" : "text-[#EF3E36]"}`}>
                    {x.kind === "entrata" ? "+" : "−"} {euro(x.amount)}
                  </td>
                  {isAdmin && (
                    <td className="p-4 text-right">
                      <button data-testid={`del-exp-${x.id}`} onClick={() => del(x.id)} className="text-[#EF3E36] p-1 hover:bg-[#FDF2F2] rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============ DOCUMENTS PANEL ============ */
function DocumentsPanel({ canManage }) {
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: "", category: "bilancio", description: "",
    fiscal_year: new Date().getFullYear(), visibility: "public", file: null,
  });

  const load = () => {
    const qs = category ? `?category=${category}` : "";
    api.get(`/transparency/docs${qs}`).then(r => setDocs(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file) { toast.error("Seleziona un file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", form.file);
      fd.append("kind", "transparency");
      const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
      const payload = {
        title: form.title, category: form.category, description: form.description,
        fiscal_year: parseInt(form.fiscal_year) || null,
        visibility: form.visibility, file_id: up.data.id,
      };
      await api.post("/transparency/docs", payload);
      toast.success("Documento pubblicato");
      setShowForm(false);
      setForm({ title: "", category: "bilancio", description: "", fiscal_year: new Date().getFullYear(), visibility: "public", file: null });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore upload");
    } finally { setUploading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo documento?")) return;
    await api.delete(`/transparency/docs/${id}`);
    toast.success("Rimosso"); load();
  };

  const downloadUrl = (id) => `${API}/transparency/docs/${id}/download`;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <select data-testid="doc-category-filter" value={category} onChange={e => setCategory(e.target.value)} className="px-4 py-2.5 rounded-xl border border-[#E8E2D8] bg-white max-w-xs">
          <option value="">Tutte le categorie</option>
          {Object.entries(TRANSPARENCY_CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {canManage && (
          <button data-testid="new-doc-btn" onClick={() => setShowForm(v => !v)} className="terracotta-btn px-4 py-2.5 rounded-xl inline-flex items-center gap-2 font-semibold">
            <Upload className="w-4 h-4" /> Carica documento
          </button>
        )}
      </div>

      {canManage && showForm && (
        <form onSubmit={submit} className="paper p-6 rounded-2xl mb-6 space-y-3" data-testid="doc-form">
          <input required data-testid="doc-title" placeholder="Titolo (es. Bilancio 2025)" value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select data-testid="doc-category" value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} className="px-4 py-2.5 rounded-lg border border-[#E8E2D8] bg-white">
              {Object.entries(TRANSPARENCY_CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input data-testid="doc-year" type="number" placeholder="Anno (opzionale)" value={form.fiscal_year} onChange={e => setForm(f => ({...f, fiscal_year: e.target.value}))} className="px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
            <select data-testid="doc-visibility" value={form.visibility} onChange={e => setForm(f => ({...f, visibility: e.target.value}))} className="px-4 py-2.5 rounded-lg border border-[#E8E2D8] bg-white">
              {Object.entries(VISIBILITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <textarea data-testid="doc-desc" placeholder="Descrizione (opzionale)" rows={2} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
          <input required data-testid="doc-file" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => setForm(f => ({...f, file: e.target.files[0]}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8] bg-white" />
          <div className="flex gap-2">
            <button type="submit" disabled={uploading} data-testid="doc-submit" className="terracotta-btn px-5 py-2.5 rounded-xl font-semibold disabled:opacity-60">
              {uploading ? "Caricamento..." : "Pubblica"}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2.5 rounded-xl border border-[#E8E2D8]">Annulla</button>
          </div>
        </form>
      )}

      <div className="grid gap-3">
        {docs.length === 0 && (
          <div className="paper p-8 rounded-2xl text-center text-[#6B6558]">
            <FileText className="w-8 h-8 mx-auto text-[#8A8272] mb-2" />
            Nessun documento pubblicato.
          </div>
        )}
        {docs.map(d => (
          <div key={d.id} data-testid={`doc-${d.id}`} className="paper p-5 rounded-2xl flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-[#FDF2F2] flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-[#EF3E36]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h4 className="font-display font-bold text-[#2B3A33]">{d.title}</h4>
                <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold rounded-full bg-[#E8EFEB] text-[#2B3A33]">
                  {TRANSPARENCY_CATEGORIES[d.category] || d.category}
                </span>
                {d.fiscal_year && <span className="px-2 py-0.5 text-[10px] font-mono rounded-full bg-[#F1F5F9] text-[#334155]">{d.fiscal_year}</span>}
                <VisBadge v={d.visibility} />
              </div>
              {d.description && <p className="text-sm text-[#6B6558] mb-1">{d.description}</p>}
              <div className="text-xs text-[#8A8272]">Caricato da {d.uploaded_by_name || "—"} · {formatDate(d.created_at)}</div>
            </div>
            <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 flex-shrink-0">
              <a href={downloadUrl(d.id)} target="_blank" rel="noreferrer" data-testid={`doc-download-${d.id}`} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#2B3A33] text-white font-semibold hover:opacity-90">
                <Download className="w-3.5 h-3.5" /> Scarica
              </a>
              {canManage && (
                <button data-testid={`doc-del-${d.id}`} onClick={() => del(d.id)} className="text-[#EF3E36] p-1.5 rounded hover:bg-[#FDF2F2]">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VisBadge({ v }) {
  const map = {
    public: { icon: Eye, label: "Pubblico", cls: "bg-[#D1FAE5] text-[#065F46]" },
    soci: { icon: UsersIcon, label: "Solo Soci", cls: "bg-[#DBEAFE] text-[#1E40AF]" },
    consiglio: { icon: EyeOff, label: "Solo Consiglio", cls: "bg-[#FEF3C7] text-[#92400E]" },
  };
  const it = map[v] || map.soci;
  const Icon = it.icon;
  return <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold rounded-full inline-flex items-center gap-1 ${it.cls}`}><Icon className="w-3 h-3" />{it.label}</span>;
}

/* ============ VERBALI PANEL ============ */
function VerbaliPanel({ canManage }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [polls, setPolls] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [expanded, setExpanded] = useState(null); // detail with votes
  const [form, setForm] = useState({
    title: "", date: new Date().toISOString().slice(0, 10),
    section: "consiglio", present_text: "", agenda: "", decisions: "",
    poll_id: "", visibility: "soci",
  });
  const [pdfMode, setPdfMode] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/verbali").then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/polls").then(r => setPolls(r.data)).catch(() => {});
  }, []);

  const resetForm = () => {
    setForm({ title: "", date: new Date().toISOString().slice(0, 10), section: "consiglio", present_text: "", agenda: "", decisions: "", poll_id: "", visibility: "soci" });
    setPdfMode(false); setPdfFile(null); setEditingId(null);
  };

  const startEdit = async (v) => {
    try {
      const r = await api.get(`/verbali/${v.id}`);
      const d = r.data;
      setForm({
        title: d.title || "", date: (d.date || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
        section: d.section || "consiglio", present_text: (d.present || []).join("\n"),
        agenda: d.agenda || "", decisions: d.decisions || "",
        poll_id: d.poll_id || "", visibility: d.visibility || "soci",
      });
      setPdfMode(false); setPdfFile(null); setEditingId(v.id); setShowForm(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { toast.error("Impossibile caricare il verbale"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Titolo obbligatorio"); return; }
    if (pdfMode && !pdfFile) { toast.error("Seleziona il PDF del verbale"); return; }
    const present = form.present_text.split("\n").map(s => s.trim()).filter(Boolean);
    try {
      if (editingId) {
        await api.put(`/verbali/${editingId}`, {
          title: form.title, date: form.date, section: form.section,
          present, agenda: form.agenda, decisions: form.decisions,
          poll_id: form.poll_id || null, visibility: form.visibility,
        });
        toast.success("Verbale aggiornato");
        setShowForm(false); resetForm(); load();
        return;
      }
      let pdf_file_id = null;
      if (pdfMode && pdfFile) {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", pdfFile);
        fd.append("kind", "verbale");
        const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        pdf_file_id = up.data.id;
        setUploading(false);
      }
      await api.post("/verbali", {
        title: form.title, date: form.date, section: form.section,
        present, agenda: form.agenda,
        decisions: pdfMode ? (form.decisions || "Verbale archiviato (PDF allegato).") : form.decisions,
        poll_id: form.poll_id || null, visibility: form.visibility,
        pdf_file_id,
      });
      toast.success(pdfMode ? "Verbale (PDF) caricato" : "Verbale pubblicato");
      setShowForm(false);
      resetForm();
      load();
    } catch (err) {
      setUploading(false);
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo verbale?")) return;
    await api.delete(`/verbali/${id}`);
    toast.success("Rimosso"); load();
  };

  const toggle = async (id) => {
    if (expandedId === id) { setExpandedId(null); setExpanded(null); return; }
    setExpandedId(id);
    try {
      const r = await api.get(`/verbali/${id}`);
      setExpanded(r.data);
    } catch { setExpanded(null); }
  };

  const pdfUrl = (id) => `${API}/verbali/${id}/pdf`;

  return (
    <div>
      <div className="flex justify-end mb-4">
        {canManage && (
          <button data-testid="new-verbale-btn" onClick={() => { if (!showForm) resetForm(); setShowForm(v => !v); }} className="terracotta-btn px-4 py-2.5 rounded-xl inline-flex items-center gap-2 font-semibold">
            <Plus className="w-4 h-4" /> Nuovo Verbale
          </button>
        )}
      </div>

      {canManage && showForm && (
        <form onSubmit={submit} className="paper p-6 rounded-2xl mb-6 space-y-3" data-testid="verbale-form">
          {!editingId && (
            <div className="flex flex-wrap gap-2 mb-1">
              <button type="button" data-testid="v-mode-compose" onClick={() => setPdfMode(false)} className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${!pdfMode ? "bg-[#2B3A33] text-white border-[#2B3A33]" : "border-[#E8E2D8] bg-white"}`}>Compila verbale</button>
              <button type="button" data-testid="v-mode-pdf" onClick={() => setPdfMode(true)} className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${pdfMode ? "bg-[#2B3A33] text-white border-[#2B3A33]" : "border-[#E8E2D8] bg-white"}`}>Carica PDF (verbale vecchio)</button>
            </div>
          )}
          {editingId && <div className="text-sm font-semibold text-[#2B3A33]" data-testid="v-edit-banner">✏️ Modifica verbale — le modifiche aggiornano anche il PDF</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input required data-testid="v-title" placeholder="Titolo (es. Assemblea 12/03/2026)" value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} className="px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
            <input required data-testid="v-date" type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} className="px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select data-testid="v-section" value={form.section} onChange={e => setForm(f => ({...f, section: e.target.value}))} className="px-4 py-2.5 rounded-lg border border-[#E8E2D8] bg-white">
              {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select data-testid="v-visibility" value={form.visibility} onChange={e => setForm(f => ({...f, visibility: e.target.value}))} className="px-4 py-2.5 rounded-lg border border-[#E8E2D8] bg-white">
              {Object.entries(VISIBILITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>

          {pdfMode ? (
            <div className="border-2 border-dashed border-[#E8E2D8] rounded-xl p-4 bg-[#FAF7F2]">
              <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">File PDF del verbale</label>
              <input data-testid="v-pdf-file" type="file" accept="application/pdf" onChange={e => setPdfFile(e.target.files?.[0] || null)} className="mt-2 block w-full text-sm" />
              {pdfFile && <p className="mt-2 text-sm text-[#2B3A33]">Selezionato: <b>{pdfFile.name}</b></p>}
              <p className="mt-2 text-xs text-[#8A8272]">Il PDF caricato verrà mostrato così com'è quando si apre il verbale. Max 10 MB.</p>
            </div>
          ) : (
            <>
              <textarea data-testid="v-present" placeholder="Elenco presenti (uno per riga)" rows={3} value={form.present_text} onChange={e => setForm(f => ({...f, present_text: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
              <textarea data-testid="v-agenda" placeholder="Ordine del giorno (da inserire manualmente)" rows={3} value={form.agenda} onChange={e => setForm(f => ({...f, agenda: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
              <textarea data-testid="v-decisions" placeholder="Decisioni / esiti votazioni (puoi aggiungerne altre manualmente)" rows={4} value={form.decisions} onChange={e => setForm(f => ({...f, decisions: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
              <div>
                <label className="text-xs uppercase tracking-widest text-[#8A8272] font-semibold">Collega a Votazione (opzionale)</label>
                <select data-testid="v-poll" value={form.poll_id} onChange={e => setForm(f => ({...f, poll_id: e.target.value}))} className="mt-1 w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8] bg-white">
                  <option value="">— Nessuna —</option>
                  {polls.map(p => <option key={p.id} value={p.id}>{p.title} ({p.total_votes || 0} voti)</option>)}
                </select>
              </div>
            </>
          )}

          <div className="flex gap-2">
            <button type="submit" disabled={uploading} data-testid="v-submit" className="terracotta-btn px-5 py-2.5 rounded-xl font-semibold disabled:opacity-60">{uploading ? "Caricamento…" : (editingId ? "Salva modifiche" : (pdfMode ? "Carica Verbale PDF" : "Pubblica Verbale"))}</button>
            <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="px-4 py-2.5 rounded-xl border border-[#E8E2D8]">Annulla</button>
          </div>
        </form>
      )}

      <div className="grid gap-3">
        {items.length === 0 && (
          <div className="paper p-8 rounded-2xl text-center text-[#6B6558]">
            <FileSignature className="w-8 h-8 mx-auto text-[#8A8272] mb-2" />
            Nessun verbale pubblicato.
          </div>
        )}
        {items.map(v => (
          <div key={v.id} data-testid={`verbale-${v.id}`} className="paper rounded-2xl overflow-hidden">
            <div className="p-5 flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#EDE9FE] flex items-center justify-center flex-shrink-0">
                <FileSignature className="w-5 h-5 text-[#5B21B6]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h4 className="font-display font-bold text-[#2B3A33]">{v.title}</h4>
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold rounded-full role-${v.section}`}>{ROLE_LABELS[v.section] || v.section}</span>
                  <VisBadge v={v.visibility} />
                </div>
                <div className="text-xs text-[#8A8272]">{formatDate(v.date)} · Redatto da {v.created_by_name || "—"}</div>
                {v.poll_id && <div className="text-xs text-[#EF3E36] font-semibold mt-1">🗳️ Contiene esito votazione</div>}
              </div>
              <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 flex-shrink-0">
                <button data-testid={`verbale-toggle-${v.id}`} onClick={() => toggle(v.id)} className="text-xs px-3 py-1.5 rounded-lg border border-[#E8E2D8] hover:bg-[#FAF7F2]">
                  {expandedId === v.id ? "Nascondi" : "Dettagli"}
                </button>
                <a href={pdfUrl(v.id)} target="_blank" rel="noreferrer" data-testid={`verbale-pdf-${v.id}`} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#2B3A33] text-white font-semibold hover:opacity-90">
                  <Download className="w-3.5 h-3.5" /> PDF
                </a>
                {canManage && !v.pdf_file_id && (
                  <button data-testid={`verbale-edit-${v.id}`} onClick={() => startEdit(v)} title="Modifica" className="text-[#2B3A33] p-1.5 rounded hover:bg-[#FAF7F2]">
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
                {canManage && (
                  <button data-testid={`verbale-del-${v.id}`} onClick={() => del(v.id)} className="text-[#EF3E36] p-1.5 rounded hover:bg-[#FDF2F2]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            {expandedId === v.id && expanded && (
              <div className="border-t border-[#E8E2D8] bg-[#FAF7F2]/60 p-5 space-y-4 text-sm">
                {expanded.present?.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#8A8272] font-bold mb-1">Presenti</div>
                    <ul className="list-disc list-inside text-[#3C4A44]">
                      {expanded.present.map((n, i) => <li key={`${i}-${n}`}>{n}</li>)}
                    </ul>
                  </div>
                )}
                {expanded.agenda && (
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#8A8272] font-bold mb-1">Ordine del giorno</div>
                    <div className="text-[#3C4A44] whitespace-pre-wrap">{expanded.agenda}</div>
                  </div>
                )}
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#8A8272] font-bold mb-1">Decisioni</div>
                  <div className="text-[#3C4A44] whitespace-pre-wrap">{expanded.decisions}</div>
                </div>
                {expanded.poll && (
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#EF3E36] font-bold mb-2">Esito Votazione: {expanded.poll.poll_title}</div>
                    <div className="grid gap-1 mb-3">
                      {expanded.poll.options?.map(opt => (
                        <div key={opt.id} className="flex justify-between text-xs bg-white rounded px-3 py-1.5 border border-[#E8E2D8]">
                          <span className="text-[#2B3A33] font-medium">{opt.text}</span>
                          <span className="font-mono text-[#6B6558]">{expanded.poll.counts?.[opt.id] || 0} voti</span>
                        </div>
                      ))}
                    </div>
                    {expanded.poll.voters?.length > 0 && (
                      <div className="bg-white rounded-lg border border-[#E8E2D8] overflow-hidden">
                        <div className="text-[10px] uppercase tracking-widest text-[#8A8272] font-bold px-3 py-2 border-b border-[#E8E2D8] bg-[#FAF7F2]">Nominativi votanti</div>
                        <table className="w-full text-xs">
                          <tbody>
                            {expanded.poll.voters.map((vt, i) => (
                              <tr key={`${vt.voter_name}-${vt.option_id || i}`} className="border-b border-[#E8E2D8]/60">
                                <td className="px-3 py-1.5 text-[#2B3A33] font-medium">{vt.voter_name}</td>
                                <td className="px-3 py-1.5 text-[#6B6558] text-right">{vt.option_text}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
